import { useEffect, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
// Hooks
import { config } from 'config';
import { useWallet } from '@/hooks/useWallet';
import {
  BIP122ChainId,
  decryptMnemonic,
  deriveBitcoinAddress,
} from '@nerdcoresdk/foundations-crypto';

// Components
import {
  AlertBody,
  CopyToClipboard,
  LoadingSpinner,
  PasswordInputIcon,
} from '@/common/index';
// Hooks
import {
  getMemberSalts,
  updateBtcAddress,
  completeBtcMigration,
} from '@/resources/wallet-service.resource';
import { useAuth } from '@/hooks/useAuth';

const initError = {
  error: false,
  message: '',
};

export default function UpdateBTCForm({ handleClose }) {
  const { fetcher, user } = useAuth();
  const { getWalletItems } = useWallet();
  const [passcode, setPasscode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(initError);
  const [encryptedData, setEncryptedData] = useState({});
  const [address, setAddress] = useState('');
  const [fetchWalletLoading, setFetchWalletLoading] = useState(false);

  const getEncryptedData = async () => {
    // get encrypted data from api
    await fetcher(getMemberSalts())
      .then((data) => {
        setEncryptedData(data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    if (user.accessToken) getEncryptedData();
  }, [user.accessToken]);

  const handleInput = (e) => {
    setError(initError);
    setPasscode(e.target.value);
  };

  const secureCleanup = () => {
    setPasscode('');
    setProcessing(false);
    setError(initError);
    setEncryptedData({});
  };

  const handleFormSubmit = async () => {
    try {
      const mnemonic = await decryptMnemonic(
        encryptedData.encryptedMnemonic,
        passcode,
        encryptedData.salt64
      );
      const network =
        config.environment.toLowerCase() === 'production'
          ? BIP122ChainId.Main
          : BIP122ChainId.Test3;
      await submitAddress(deriveBitcoinAddress(mnemonic, network));
    } catch {
      setError({
        error: true,
        message: 'Decryption failed, please verify the passcode and try again.',
      });
    }
  };

  const submitAddress = async (addressRes) => {
    await fetcher(updateBtcAddress({ btcAddress: addressRes }))
      .then(async () => {
        await fetcher(completeBtcMigration())
          .then(() => {
            setAddress(addressRes);
            setProcessing(false);
            secureCleanup();
          })
          .catch(() => {
            setAddress('');
            setError({
              error: true,
              message: 'Unable to complete BTC migration. Please try again.',
            });
          });
      })
      .catch(() => {
        setAddress('');
        setError({
          error: true,
          message: 'Unable to update BTC address. Please try again.',
        });
      });
  };

  const completeMigration = async () => {
    setFetchWalletLoading(true);
    // refresh the wallet data and close
    await getWalletItems();
    handleClose();
  };

  return (
    <Box sx={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {error.error && (
        <Alert severity="error">
          <AlertBody header="Error" body={error.message} />
        </Alert>
      )}
      {!address ? (
        <>
          <Typography variant="body1" data-core-qa="btcPasscodeText">
            Please enter your wallet passcode to update your Bitcoin address.
          </Typography>
          <TextField
            label="Wallet Passcode"
            id="passcode"
            size="small"
            data-core-qa="btcPasscodeInput"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter wallet passcode"
            value={passcode}
            onChange={(e) => {
              handleInput(e);
            }}
            InputProps={{
              endAdornment: (
                <PasswordInputIcon state={showPassword} setState={setShowPassword} />
              ),
            }}
            fullWidth
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              gap: '16px',
              alignItems: 'center',
              marginTop: '16px',
            }}
          >
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleFormSubmit} disabled={!passcode}>
              {processing ? <LoadingSpinner /> : 'Submit'}
            </Button>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography variant="body1" data-core-qa="btcPasscodeText">
            Your new BTC address is:
          </Typography>
          <CopyToClipboard copyText={address} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              gap: '16px',
              alignItems: 'center',
              marginTop: '16px',
            }}
          >
            <Button
              variant="contained"
              onClick={completeMigration}
              disabled={fetchWalletLoading}
            >
              {fetchWalletLoading ? <LoadingSpinner /> : 'Done '}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
